import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogCard from '../../../components/blog/BlogCard';
import { IconButton, Container, SectionTitle } from '../../../components/ui';
import { apiUrl } from '../../../config/api';
import makanan from '../../../images/Makanan-dan-Minuman.png';
import buah from '../../../images/Buah-dan-Sayuran.png';
import susu from '../../../images/Olahan-Susu-dan-Makanan-Beku.png';
import rumah from '../../../images/Perlengkapan-Rumah-Tangga.png';

const CARD_WIDTH = 320;
const CARD_GAP = 20;

const slugToFallbackImage = { food: makanan, fresh: buah, snack: susu, household: rumah };
const slugToCategoryLabel = { food: 'Food', fresh: 'Fresh', snack: 'Snack', household: 'Household' };

const defaultCards = [
  { image: makanan, category: 'Food', title: 'Food', excerpt: 'Makanan & minuman.', slug: 'food' },
  { image: buah, category: 'Fresh', title: 'Fresh', excerpt: 'Buah, sayuran & bahan segar.', slug: 'fresh' },
  { image: susu, category: 'Snack', title: 'Snack', excerpt: 'Cemilan & makanan ringan.', slug: 'snack' },
  { image: rumah, category: 'Household', title: 'Household', excerpt: 'Perlengkapan rumah tangga.', slug: 'household' },
];

export default function CategorySection() {
  const [cards, setCards] = useState(defaultCards);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/categories/public'))
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const ordered = ['food', 'fresh', 'snack', 'household'];
        const list = ordered.map((slug) => {
          const c = data.find((x) => x.slug === slug) || { slug, title: slugToCategoryLabel[slug] || slug, excerpt: '' };
          return {
            slug: c.slug,
            category: slugToCategoryLabel[c.slug] || c.title,
            title: c.title || slugToCategoryLabel[c.slug] || c.slug,
            excerpt: c.excerpt || '',
            image: c.imageUrl || slugToFallbackImage[c.slug] || makanan,
          };
        });
        setCards(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const shift = index * (CARD_WIDTH + CARD_GAP);
  const maxIndex = Math.max(0, cards.length - 1);
  const canPrev = index > 0;
  const canNext = index < maxIndex;
  const handlePrev = () => setIndex((i) => Math.max(0, i - 1));
  const handleNext = () => setIndex((i) => Math.min(maxIndex, i + 1));

  return (
    <section id="kategori" className="py-16 md:py-20 bg-am-green-50/50">
      <Container>
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide text-am-accent-700 bg-am-green-100 text-am-text-900 mb-4">
            Katalog
          </span>
          <SectionTitle
            title="Kategori Produk Kami Ada di Sini!"
            subtitle="Temukan apa yang Anda butuhkan."
            align="center"
            className="mb-2"
          />
        </div>

        <div className="relative -mx-2 md:-mx-4">
          {/* Gradient fade kiri & kanan */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-12 z-10 w-16 md:w-24 bg-gradient-to-r from-am-green-50/50 to-transparent" aria-hidden />
          <div className="pointer-events-none absolute right-0 top-0 bottom-12 z-10 w-16 md:w-24 bg-gradient-to-l from-am-green-50/50 to-transparent" aria-hidden />

          <div className="overflow-hidden px-2 md:px-4">
            <div
              className="flex gap-5 py-2 transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${shift}px)` }}
            >
              {cards.map((card) => (
                <Link key={card.slug} to={`/kategori/${card.slug}`} className="flex-shrink-0">
                  <BlogCard
                    image={card.image}
                    category={card.category}
                    title={card.title}
                    excerpt={card.excerpt}
                  />
                </Link>
              ))}
            </div>
          </div>

          {/* Tombol prev/next */}
          <div className="absolute inset-y-0 left-0 flex items-center pl-0 md:pl-2">
            <IconButton
              variant="default"
              shape="round"
              size="md"
              className={`hidden md:inline-flex shadow-md transition-all ${canPrev ? 'opacity-100 hover:bg-am-accent hover:text-white' : 'opacity-40 cursor-not-allowed'}`}
              aria-label="Kategori sebelumnya"
              onClick={handlePrev}
              disabled={!canPrev}
            >
              ‹
            </IconButton>
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center pr-0 md:pr-2">
            <IconButton
              variant="default"
              shape="round"
              size="md"
              className={`hidden md:inline-flex shadow-md transition-all ${canNext ? 'opacity-100 hover:bg-am-accent hover:text-white' : 'opacity-40 cursor-not-allowed'}`}
              aria-label="Kategori berikutnya"
              onClick={handleNext}
              disabled={!canNext}
            >
              ›
            </IconButton>
          </div>
        </div>

        {/* Dot indicator */}
        <div className="flex justify-center gap-2 mt-8" aria-hidden>
          {cards.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === index ? 'bg-am-accent scale-125' : 'bg-am-green-200 hover:bg-am-accent/70'
              }`}
              aria-label={`Ke kategori ${i + 1}`}
            />
          ))}
        </div>
      </Container>
    </section>
  );
}
